/**
 * Checkers (English draughts) engine (WS-17d). Pure, side-effect-free, exhaustively unit
 * tested — same move-log/replay pattern as `connect4.ts` (see that file's docblock for the
 * CRDT rationale this mirrors): a `game:<id>` doc holds a single append-only
 * `Y.Array<CheckersMove>`, never a mutable board, and `deriveCheckers` is the only authority
 * on what's true — it re-derives turn order, legality, and outcome from the moves it has
 * already accepted, never from a move's mere presence in the array.
 *
 * **Rules encoded here (the classic/American ruleset, not international draughts):**
 *  - 8x8 board, 12 pieces per side on the dark squares of the first three rows on each end.
 *    Player 0 starts on rows 0-2 and moves toward increasing row; player 1 starts on rows
 *    5-7 and moves toward decreasing row.
 *  - **Forced capture.** If the player to move has *any* legal capture available (from any
 *    of their pieces), only capture moves are legal that turn — a simple non-capture move is
 *    rejected at derive time even if it's otherwise a legal diagonal step. This is the
 *    classic rule, not a house variant.
 *  - **Multi-jump as a single move entry.** A capturing turn is one `{ from, path }` move,
 *    where `path` is the full chain of landing squares (not the captured squares) —
 *    the simplest CRDT shape: one array entry per turn, no matter how many pieces get
 *    swept up along the way. Continuing to jump is *mandatory* once started (English
 *    draughts, not "may stop early"), but *which* capture to take at a branch point (a
 *    different piece, or a fork mid-sequence) is the player's choice — `deriveCheckers`
 *    validates the exact path the move claims against every maximal capture sequence the
 *    board allows, and accepts only an exact match.
 *  - **Kinging stops the jump.** If a man lands on the opponent's back rank mid-sequence, it
 *    is crowned immediately and the jump sequence ends right there, even if the newly-made
 *    king could technically continue capturing from that square. This is the classic ruling
 *    (a piece doesn't "jump as a king" the same turn it's crowned) — encoded by
 *    `generateCaptureSequences` simply not recursing past a landing that crowns the piece.
 *  - **Kings** move and capture diagonally in all four directions, not just forward.
 *  - **Win** = the opponent has no pieces left, or no legal move at all (including no legal
 *    capture) on their turn.
 *  - **Draw**: either player may offer (`kind: 'draw-offer'`) and the other may accept
 *    (`kind: 'draw-accept'`) — both are move-log entries, neither consumes a turn, and any
 *    ordinary move played after an offer silently cancels it (the next `derive` pass never
 *    sees a stale pending offer). There's also an automatic draw after 40 half-moves
 *    (individual turns by either player) pass with no capture — the stalemate-grind
 *    safeguard, checked the instant the 40th such move lands, no explicit move needed.
 *  - **Replay-order-wins.** Exactly like Connect-4: an illegal/out-of-turn/malformed entry
 *    is skipped (not applied, not counted, doesn't advance turn) and replay continues —
 *    a stray/duplicate/offline-race entry never derails the rest of the game.
 */
export const CHECKERS_SIZE = 8;
const MAN_DIRECTIONS = {
    0: [
        [1, -1],
        [1, 1],
    ],
    1: [
        [-1, -1],
        [-1, 1],
    ],
};
const KING_DIRECTIONS = [
    [1, -1],
    [1, 1],
    [-1, -1],
    [-1, 1],
];
function directionsFor(piece) {
    return piece.king ? KING_DIRECTIONS : MAN_DIRECTIONS[piece.player];
}
function inBounds(row, col) {
    return row >= 0 && row < CHECKERS_SIZE && col >= 0 && col < CHECKERS_SIZE;
}
/** The row that crowns a man belonging to `player` — the far end from where they started. */
function isBackRank(player, row) {
    return player === 0 ? row === CHECKERS_SIZE - 1 : row === 0;
}
function cloneBoard(board) {
    return board.map((row) => row.slice());
}
function cellAt(board, coord) {
    return board[coord[0]]?.[coord[1]] ?? null;
}
/** The starting position: 12 pieces per side on the dark squares of their first three rows. */
export function initialBoard() {
    const board = Array.from({ length: CHECKERS_SIZE }, () => Array(CHECKERS_SIZE).fill(null));
    for (let r = 0; r < 3; r += 1) {
        for (let c = 0; c < CHECKERS_SIZE; c += 1) {
            if ((r + c) % 2 === 1)
                board[r][c] = { player: 0, king: false };
        }
    }
    for (let r = CHECKERS_SIZE - 3; r < CHECKERS_SIZE; r += 1) {
        for (let c = 0; c < CHECKERS_SIZE; c += 1) {
            if ((r + c) % 2 === 1)
                board[r][c] = { player: 1, king: false };
        }
    }
    return board;
}
/** Every empty-landing diagonal step available to the piece at `pos` — no captures. */
export function simpleDestinations(board, pos, piece) {
    const out = [];
    for (const [dr, dc] of directionsFor(piece)) {
        const r = pos[0] + dr;
        const c = pos[1] + dc;
        if (inBounds(r, c) && cellAt(board, [r, c]) === null)
            out.push([r, c]);
    }
    return out;
}
/**
 * Every *maximal* capture sequence available to a piece starting at `pos` (a piece already
 * mid-sequence carries its current `pathSoFar`). Recurses through the board with captured
 * pieces removed and the piece relocated, backtracking per branch so sibling branches don't
 * see each other's simulated state. Stops recursing — yields a terminal sequence — the
 * instant either (a) no further capture is available from the new landing, or (b) the piece
 * was just crowned (see the module docblock's "kinging stops the jump" rule).
 */
export function generateCaptureSequences(board, pos, player, isKing, pathSoFar = []) {
    const dirs = isKing ? KING_DIRECTIONS : MAN_DIRECTIONS[player];
    const captures = [];
    for (const [dr, dc] of dirs) {
        const mid = [pos[0] + dr, pos[1] + dc];
        const dest = [pos[0] + 2 * dr, pos[1] + 2 * dc];
        if (!inBounds(dest[0], dest[1]))
            continue;
        const midPiece = cellAt(board, mid);
        if (!midPiece || midPiece.player === player)
            continue;
        if (cellAt(board, dest) !== null)
            continue;
        captures.push({ mid, dest });
    }
    if (captures.length === 0) {
        return pathSoFar.length > 0 ? [pathSoFar] : [];
    }
    const sequences = [];
    for (const cap of captures) {
        const next = cloneBoard(board);
        next[pos[0]][pos[1]] = null;
        next[cap.mid[0]][cap.mid[1]] = null;
        const becomesKing = !isKing && isBackRank(player, cap.dest[0]);
        next[cap.dest[0]][cap.dest[1]] = { player, king: isKing || becomesKing };
        const nextPath = [...pathSoFar, cap.dest];
        if (becomesKing) {
            sequences.push(nextPath);
        }
        else {
            sequences.push(...generateCaptureSequences(next, cap.dest, player, isKing, nextPath));
        }
    }
    return sequences;
}
/** True iff `player` has at least one legal capture available anywhere on the board — the
 * forced-capture gate. */
export function anyCaptureAvailable(board, player) {
    for (let r = 0; r < CHECKERS_SIZE; r += 1) {
        for (let c = 0; c < CHECKERS_SIZE; c += 1) {
            const piece = board[r]?.[c];
            if (!piece || piece.player !== player)
                continue;
            if (generateCaptureSequences(board, [r, c], player, piece.king).length > 0)
                return true;
        }
    }
    return false;
}
/** Every legal move `player` can make right now — captures only when the forced-capture rule
 * is active, simple steps otherwise. What the UI highlights after a piece is tapped. */
export function legalMovesForPlayer(board, player) {
    const forced = anyCaptureAvailable(board, player);
    const out = [];
    for (let r = 0; r < CHECKERS_SIZE; r += 1) {
        for (let c = 0; c < CHECKERS_SIZE; c += 1) {
            const piece = board[r]?.[c];
            if (!piece || piece.player !== player)
                continue;
            if (forced) {
                for (const seq of generateCaptureSequences(board, [r, c], player, piece.king)) {
                    out.push({ from: [r, c], path: seq });
                }
            }
            else {
                for (const dest of simpleDestinations(board, [r, c], piece)) {
                    out.push({ from: [r, c], path: [dest] });
                }
            }
        }
    }
    return out;
}
function pathsEqual(a, b) {
    if (a.length !== b.length)
        return false;
    return a.every((coord, i) => coord[0] === b[i]?.[0] && coord[1] === b[i]?.[1]);
}
/** Auto-draw threshold: half-moves (either player's ordinary turns) since the last capture. */
const NO_CAPTURE_DRAW_LIMIT = 40;
/**
 * Replays `moves` in order against the starting position, validating each one before it
 * counts. See the module docblock for the full rule set. An invalid/out-of-turn/malformed
 * entry is skipped (not applied, not counted, doesn't advance turn) and replay continues.
 */
export function deriveCheckers(moves, playerIds) {
    let board = initialBoard();
    let turnIndex = 0;
    let winner = null;
    let isDrawFlag = false;
    let drawOfferBy = null;
    let movesSinceCapture = 0;
    const captured = [0, 0];
    const rejectedMoveIndexes = [];
    const playerIndexOf = (id) => {
        if (id === playerIds[0])
            return 0;
        if (id === playerIds[1])
            return 1;
        return null;
    };
    for (let i = 0; i < moves.length; i += 1) {
        const move = moves[i];
        const decided = winner !== null || isDrawFlag;
        if (!move || decided) {
            rejectedMoveIndexes.push(i);
            continue;
        }
        if (move.kind === 'draw-offer') {
            const offeringIndex = playerIndexOf(move.playerId);
            if (offeringIndex === null) {
                rejectedMoveIndexes.push(i);
                continue;
            }
            drawOfferBy = offeringIndex;
            continue;
        }
        if (move.kind === 'draw-accept') {
            const acceptingIndex = playerIndexOf(move.playerId);
            if (drawOfferBy === null || acceptingIndex === null || acceptingIndex === drawOfferBy) {
                rejectedMoveIndexes.push(i);
                continue;
            }
            isDrawFlag = true;
            continue;
        }
        // Ordinary piece move.
        if (move.playerId !== playerIds[turnIndex]) {
            rejectedMoveIndexes.push(i);
            continue;
        }
        const piece = cellAt(board, move.from);
        if (!piece || piece.player !== turnIndex || !move.path || move.path.length === 0) {
            rejectedMoveIndexes.push(i);
            continue;
        }
        const forced = anyCaptureAvailable(board, turnIndex);
        const candidates = forced
            ? generateCaptureSequences(board, move.from, turnIndex, piece.king)
            : simpleDestinations(board, move.from, piece).map((dest) => [dest]);
        const matches = candidates.some((seq) => pathsEqual(seq, move.path));
        if (!matches) {
            rejectedMoveIndexes.push(i);
            continue;
        }
        const next = cloneBoard(board);
        next[move.from[0]][move.from[1]] = null;
        let cur = move.from;
        let king = piece.king;
        if (forced) {
            for (const landing of move.path) {
                const mid = [(cur[0] + landing[0]) / 2, (cur[1] + landing[1]) / 2];
                next[mid[0]][mid[1]] = null;
                captured[turnIndex] += 1;
                king = king || isBackRank(turnIndex, landing[0]);
                cur = landing;
            }
            movesSinceCapture = 0;
        }
        else {
            const dest = move.path[0];
            king = king || isBackRank(turnIndex, dest[0]);
            cur = dest;
            movesSinceCapture += 1;
        }
        next[cur[0]][cur[1]] = { player: turnIndex, king };
        board = next;
        drawOfferBy = null; // an ordinary move cancels any pending offer
        const mover = turnIndex;
        const opponent = mover === 0 ? 1 : 0;
        const opponentHasPieces = board.some((row) => row.some((cell) => cell?.player === opponent));
        if (!opponentHasPieces) {
            winner = mover;
        }
        else if (legalMovesForPlayer(board, opponent).length === 0) {
            winner = mover;
        }
        else {
            turnIndex = opponent;
            if (movesSinceCapture >= NO_CAPTURE_DRAW_LIMIT)
                isDrawFlag = true;
        }
    }
    const gameOver = winner !== null || isDrawFlag;
    return {
        board,
        appliedMoves: moves.length - rejectedMoveIndexes.length,
        rejectedMoveIndexes,
        turn: gameOver ? null : turnIndex,
        winner,
        isDraw: isDrawFlag,
        drawOfferBy: gameOver ? null : drawOfferBy,
        captured,
        forcedCapture: gameOver ? false : anyCaptureAvailable(board, turnIndex),
    };
}
//# sourceMappingURL=checkers.js.map